<template>
    <PtDialog
        :visible="true"
        :lock-scroll="false"
        width="480px"
        class="clue-report-dialog"
        :title="editFormTitle"
        @close="cancelEditBuyInfo"
        :showConfirmButton="false"
    >
        <div class="edit-buy__content" v-if="!isOversea">
            <template v-if="step === 1">
                <p class="edit-buy__tip">{{titleKey ? t('updateTip.editForm.overlimitSub') : t('updateTip.editForm.subTitle')}}</p>
                <ElForm :model="buyInfo" :rules="rules" ref="editBuyInfoForm"
                        label-position="top"
                        class="edit-buy__dialog-form">

                    <ElFormItem :label="t('updateTip.editForm.name')" prop="name">
                        <ElInput :disabled="!!savedInfo.full_name" v-model="buyInfo.name" :placeholder="t('updateTip.editForm.name_p')"></ElInput>
                    </ElFormItem>

                    <ElFormItem :label="t('updateTip.editForm.phone')" prop="mobile">
                        <ElInput v-if="maskedMobile" disabled :value="maskedMobile" />
                        <ElInput v-else :disabled="!!savedInfo.mobile_number" v-model="buyInfo.mobile" :placeholder="t('updateTip.editForm.phone_p')"
                                 onkeydown="return (/[\d]/.test(String.fromCharCode(event.keyCode)))" />
                    </ElFormItem>

                    <ElFormItem v-if="!savedInfo.mobile_number" prop="code" class="edit-buy__sms">
                        <ElInput v-model="buyInfo.code"
                                 onKeypress="return (/[\d]/.test(String.fromCharCode(event.keyCode)))"
                                 :placeholder="t('updateTip.editForm.sms_p')">
                            <template #suffix>
                                <button
                                    class="th-btn th-btn-text edit-buy__sms-btn"
                                    type="button"
                                    @click="showGeetestDialog"
                                    :disabled="initTimer > 0 || loading"
                                >
                                    {{initTimer > 0 ? `${initTimer}s` : sendText}}
                                </button>
                            </template>
                        </ElInput>

                    </ElFormItem>

                    <ElFormItem :label="t('updateTip.editForm.company')" prop="company_name">
                        <ElInput class="absolute scale-50" v-model="buyInfo.company_name" :placeholder="t('updateTip.editForm.company_p')"></ElInput>
                        <CompanySearch v-if="!savedInfo.company_name" :value="buyInfo.company_name" @blur="handleCompanyBlur" @input="buyInfo.company_name = $event" :poperWidth="464" :placeholder="t('updateTip.editForm.company_p')" class="w-full relative" />
                        <ElInput v-else :disabled="true" :value="savedInfo.company_name"></ElInput>
                    </ElFormItem>

                    <ElFormItem :label="t('updateTip.editForm.model')" prop="post">
                        <ElInput :disabled="!!savedInfo.job_role" v-model="buyInfo.post" :placeholder="t('updateTip.editForm.model_p')"></ElInput>
                    </ElFormItem>

                    <ElFormItem v-if="reasonOptions.length > 0" :label="t('updateTip.editForm.reason')" prop="reason">
                        <ElCheckboxGroup v-model="buyInfo.reason" class="flex flex-col gap-2">
                            <ElCheckbox v-for="option in reasonOptions" :key="option" :label="option" :value="option" />
                            <ElCheckbox label="other" value="other">
                                {{ t('updateTip.editForm.other') }}
                            </ElCheckbox>
                        </ElCheckboxGroup>
                    </ElFormItem>

                    <ElFormItem v-if="buyInfo.reason.includes('other')" class="-mt-4"
                                prop="otherReason">
                        <ElInput v-model="buyInfo.otherReason"
                                 :placeholder="t('updateTip.editForm.otherReason_p')" />
                    </ElFormItem>

                </ElForm>

                <div flex="o" class="edit-buy__footer">
                    <button class="th-btn"
                            :disabled="formDisabled"
                            @click="submitInfo">
                        <span v-if="!loading">{{t('updateTip.editForm.submit')}}</span>
                        <PtIcon style="font-size: 24px" icon="SolidLoadingBars" v-else/>
                    </button>
                </div>
            </template>
            <CnReportStatus v-else :resultStatus="cnReportStatus" @cancel="cancelEditBuyInfo" />
        </div>

        <div class="edit-buy__content" v-else>
            <p class="edit-buy__tip">{{titleKey ? t('updateTip.editForm.overlimitSub') : 'Leave your info, we will contact you as soon as possible.'}}</p>
            <div style="min-height:500px;" id="hubspotForm"></div>
        </div>
    </PtDialog>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref, onBeforeUnmount } from 'vue';
import _ from 'lodash';
import { useI18n } from 'vue-i18n';
import { PtDialog, Toast, PtIcon } from 'patsnap-biz';
import { ElFormItem, ElInput, ElForm, ElCheckboxGroup, ElCheckbox, type FormInstance } from 'element-plus';
import { geetestValidate, geetestEnabledErrorCode } from '@eureka/utils/geetest';
import CompanySearch from '@eureka/shared/upgrade-activity/src/components/CompanySearch.vue';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import '@patsnap-ui/icon/assets/solid/tick.svg';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import JSEncrypt from 'jsencrypt';
import { E_MobileVerifyCodePurpose } from '@eureka/services/src/apis/common.api.types'
import { HUBSPOT_REGION, GEETEST_MESSAGEAUTHENTICATION, SMS_CODE_ERROR, HUBSPOT_IFRAME_URL } from '../constants';
import type { IOpenClueReportOptions } from '../types';
import type { AccountInfoCn, IClueForm } from '@eureka/services/src/apis/common.api.types';
import { E_LanguageLong } from '../types';
import { loadScript } from '@eureka/utils/load-script';
import CnReportStatus from './CnReportStatus.vue';
import { useBiTrack } from '../../../composables/use-bi-track';
import { BiTrackPoint, AIR_UPGRADE_EVENT_TYPE } from '../../../edition-upgrade/src/constants';
import { DeployEnv } from '@patsnap-utils/biz';
import { REGION } from '@eureka/utils/environment';
import { maskPhoneNumber } from '@eureka/shared/upgrade-activity/src/phone-mask-utils';

const { t, tm, locale } = useI18n();

const { biTrack } = useBiTrack(AIR_UPGRADE_EVENT_TYPE);

const isOversea = computed(() => [DeployEnv.US, DeployEnv.EU].includes(REGION))

const props = defineProps<IOpenClueReportOptions>();

const savedInfo = reactive<AccountInfoCn>({
    company_name: '',
    job_role: '',
    full_name: '',
    mobile_number: '',
})

const buyInfo = reactive<IClueForm>({
    company_name: '',
    post: '',
    name: '',
    mobile: '',
    code: '',
    reason: [],
    otherReason: '',
})

const cnReportStatus = ref<boolean>(false) // 作为初始值不显示UI的判断

const step = ref(1) // 默认第一步

const loading = ref(false)
const sendFlag = ref(false)

const formDisabled = computed(() => {
    const values = Object.values(buyInfo);
    const final = _.compact(values);
    return !final.length || loading.value;
})

const reasonOptions = computed(() => {
    return tm(`updateTip.editForm.reasons.${props.version}`);
})

const maskedMobile = computed(() => {
    return maskPhoneNumber(savedInfo.mobile_number);
})

const editFormTitle = computed(() => {
    return isOversea.value ? (props.titleKey ? t('updateTip.editForm.overlimit') : 'Chat with us') : t('updateTip.editForm.title')
})

const sendText = computed(() => {
    if (sendFlag.value) {
        return t('updateTip.editForm.resend')
    } else {
        return t('updateTip.editForm.send_sms')
    }
})

const editBuyInfoForm = ref<FormInstance | null>(null)

function handleCompanyBlur() {
    editBuyInfoForm.value?.validateField(['company_name']);
}

const validatePhoneNumber = (value:string) => {
    const reg = /^1[0-9]{10}$/;
    return reg.test(value)
}

const validateSMSCode = (value:string) => {
    const reg = /^[0-9]{6}$/;
    return reg.test(value)
}
const rules = {
    name: [
        { required: true, message: t('updateTip.editForm.name_requied'), trigger: 'blur' },
        { max: 100, message: t('updateTip.editForm.name_length_error'), trigger: 'blur' }
    ],
    mobile: [
        { required: true, message: t('updateTip.editForm.phone_required'), trigger: 'blur' },
        // 手机号码格式
        {
            validator: (rule:any, value:string, callback:any) => {
                if (!validatePhoneNumber(value)) {
                    callback(new Error(t('updateTip.editForm.phone_pattern_error')));
                    return false;
                } else {
                    return callback();
                }
            },
            trigger: 'blur',
        }
    ],
    code: [
        { required: true, message: t('updateTip.editForm.sms_required'), trigger: 'blur' },
        // 验证码格式
        {
            validator: (rule:any, value:string, callback:any) => {
                if (!validateSMSCode(value)) {
                    callback(new Error(t('updateTip.editForm.sms_pattern_error')));
                    return false;
                } else {
                    return callback();
                }
            },
            trigger: 'blur',
        }
    ],
    company_name: [
        { required: true, message: t('updateTip.editForm.company_required'), trigger: 'blur' },
        { max: 256, message: t('updateTip.editForm.company_name_length_error'), trigger: 'blur' }
    ],
    post: [
        { required: true, message: t('updateTip.editForm.model_required'), trigger: 'blur' },
        { max: 100, message: t('updateTip.editForm.post_length_error'), trigger: 'blur' }
    ],
    reason: [
        {
            required: true,
            message: t('updateTip.editForm.reason_required'),
            trigger: 'change',
            validator: (rule: any, value: string[], callback: any) => {
                if (value.length === 0) {
                    callback(new Error(t('updateTip.editForm.reason_required')));
                } else {
                    callback();
                }
            },
        }
    ],
    otherReason: [
        {
            required: true,
            message: t('updateTip.editForm.otherReason_required'),
            trigger: ['blur', 'change'],
            validator: (rule: any, value: string, callback: any) => {
                if (buyInfo.reason.includes('other') && !value.trim()) {
                    callback(new Error(t('updateTip.editForm.otherReason_required')));
                } else {
                    callback();
                }
            },
        }
    ],
}

const initTimer = ref(0);
let intervalTimer: number | null = null;

onMounted(async() => {
    if (isOversea.value) {
        const hubspotData = {
            region: HUBSPOT_REGION,
            portalId: props.hubspotPortalId,
            formId: props.hubspotFormId,
            target: '#hubspotForm',
        }
        /**
         * us下需要加载hobspot js sdk
         */
        // @ts-ignore
        if (window.hbspt) {
            // @ts-ignore
            window.hbspt.forms.create(hubspotData);
        } else {
            loadScript(HUBSPOT_IFRAME_URL, true).then(() => {
                // @ts-ignore
                window.hbspt.forms.create(hubspotData);
            }).catch(() => {
                Toast({
                    type: 'error',
                    message: 'load hubspot failed',
                    duration: 5000,
                    showClose: true,
                });
            });
        }
    } else {
        const info = await CommonApis.getAccountInfo();
        buyInfo.mobile = savedInfo.mobile_number = info.mobile_number;
        buyInfo.company_name = savedInfo.company_name = info.company_name;
        buyInfo.post = savedInfo.job_role = info.job_role;
        buyInfo.name = savedInfo.full_name = info.full_name;
    }
})

function showReportStatus(resultStatus = false) {
    cnReportStatus.value = resultStatus;
    step.value = 2;
}

function cancelEditBuyInfo() {
    props?.onClose?.();
    step.value = 1;
}

async function sendSmsCode(resGeetestValidate = {}) {
    loading.value = true;
    try {
        const publicKey = await CommonApis.getPublicKeyApiByAnonymous()
        const encrypt = new JSEncrypt({})
        encrypt.setPublicKey(publicKey)
        const encryptMobileNumber = encrypt.encrypt(buyInfo.mobile as string)
        await CommonApis.postMobileVerifyCodeApiByAnonymous({
            purpose: E_MobileVerifyCodePurpose.SMS_SALES_LEAD,
            country_code: '+86',
            mobile_number: encryptMobileNumber as string,
            language: _.get(E_LanguageLong, locale.value),
            ...resGeetestValidate,
        })
        loading.value = false;
        // @ts-ignore
        Toast({
            type: 'success',
            message: t('updateTip.editForm.send_success', { phone: buyInfo.mobile }),
            duration: 5000,
            showClose: true,
        })
        initTimer.value = 60;
        sendFlag.value = true;
        intervalTimer = setInterval(() => {
            if (initTimer.value === 0) {
                return;
            }
            initTimer.value--;
        }, 1000)
    } catch (error) {
        // @ts-ignore
        Toast({
            type: 'error',
            message: t('updateTip.editForm.send_failed'),
            duration: 5000,
            showClose: true,
        })
        loading.value = false;
    }
}

function showGeetestDialog() {
    editBuyInfoForm.value?.validateField(['mobile'], (isValid) => {
        if (!isValid) {
            return false;
        } else {
            // @ts-ignore
            geetestValidate({
                captchaId: GEETEST_MESSAGEAUTHENTICATION,
                outside: false,
                hideClose: false,
                lang: locale.value,
            }).catch((geeErr: any) => {
                // 极验出错，前端放行
                const geeErrCode = geeErr.code;
                if (geetestEnabledErrorCode.includes(geeErrCode)) {
                    return Promise.resolve({});
                }
                return Promise.reject(geeErr);
            }).then(sendSmsCode);
        }
    })
}

async function submitInfo() {
    try {
        const formValid = await editBuyInfoForm.value?.validate();
        if (!formValid) return;
        try {
            loading.value = true;
            const formData = savedInfo.mobile_number ? _.omit(buyInfo, ['mobile', 'code']) : buyInfo;
            const res = await CommonApis.submitEurekaUpdateInfo({ ...formData, type: props.packageType, source_type: props.sourceType });
            loading.value = false;
            if (res) {
                showReportStatus(true);
                biTrack({
                    sourceType: BiTrackPoint.AIR_UPGRADE_CLUEREPORT_SUBMIT,
                    otherParam: props.version,
                    eventDetailStr: props.trackParams,
                });
            }
        } catch (err) {
            const errorCode = _.get(err, 'data.error_code') || 400;
            // @ts-ignore
            if (errorCode === SMS_CODE_ERROR) {
            // @ts-ignore
                Toast({
                    message: t(`clueValidate.${errorCode}`),
                    type: 'error',
                    duration: 5000,
                    showClose: true,
                });
            } else {
                showReportStatus(false);
            }
            loading.value = false;
        }
    } catch (error) {}
}

onBeforeUnmount(() => {
    if (intervalTimer) {
        clearInterval(intervalTimer)
        intervalTimer = null
    }
    _.each(window.hbspt.forms.shellObservers, o => o.disconnect())
    window.hbspt.forms.shellObservers = [];
    window.hbspt.forms.shells = 0;
})

</script>

<style lang="scss">
.edit-buy__content {
    color: $text-primary;
}

.edit-buy__tip {
    font-size: 14px;
    color: $text-hint;
    margin: 24px 0;
}

.edit-buy__dialog-form {
    .el-form-item {
        &:not(:last-child) {
            margin-bottom: 16px;
        }

        .el-form-item__label {
            padding-bottom: 3px;
            line-height: 20px;
        }

        .el-input {
            height: 38px;
            width: 100%;
            margin-top: 3px;
            .el-input__inner {
                padding-left: 12px;
            }
        }
    }

    .el-form-item.is-error {
        &:not(:last-child) {
            margin-bottom: 32px;
        }
    }

    .el-checkbox {
        @apply font-normal text-[var(--el-text-color-regular)] h-4 #{!important};
        &.is-checked {
            .el-checkbox__label {
                @apply font-normal text-[var(--el-text-color-regular)] #{!important};
            }
        }
    }

    .company-search {
        input {
            border: none;
            box-shadow: 0 0 0 1px var(--el-input-border-color,var(--el-border-color)) inset;
            transition-property: box-shadow;
            color: var(--el-input-text-color, var(--el-text-color-regular));
            padding-left: 24px;
            &:focus {
                box-shadow: 0 0 0 1px #0764E9 inset;
            }
            &::placeholder {
                color: var(--el-text-color-placeholder);
            }
        }
    }
    .is-error {
        .company-search {
            input {
                box-shadow: 0 0 0 1px var(--el-color-danger) inset;
            }
        }
    }

    .edit-buy__sms {
        .el-input__suffix {
            button {
                border: none;
                &:hover {
                    background: $bg-white;
                    border: none;
                }
            }
        }
        &-btn {
            font-size: 12px;
            color: $brand_primary !important;
            cursor: pointer;
            &[disabled] {
                cursor: not-allowed;
            }
        }
    }
}
.clue-report-dialog {
    .th-modal-action {
        display: none;
    }
}

.edit-buy__footer {
    margin-top: 24px;
    button {
        width: 100%;
        height: 40px;
    }
}
</style>
